import Highcharts from 'highcharts'

class RiskReturnChart {
  constructor(data) {
    this.data = data || {}
  }

  _fixFloatingPoint(value, decimalPlaces = 4) {
    return parseFloat(value.toFixed(decimalPlaces))
  }

  /**
   * Given two coordinates, returns the proper minimum and maximum needed
   * @param primaryCoord
   * @param secondaryCoord
   * @param i // 0 = x, 1 = y
   * @returns {{min: number, max: *}}
   */
  _getMinMax(primaryCoord, secondaryCoord, i) {
    const n1 = primaryCoord[i]
    const n2 = secondaryCoord[i]

    let min = 2 * n1 - n2
    let max = n2

    if (n1 > n2) {
      let tmp = min
      min = max
      max = tmp
    }

    return {
      min: min,
      max: max,
    }
  }

  _getCoordinates(primary, secondary, padding) {
    const primaryCoordinate = [primary.x, primary.y]
    const secondaryCoordinate = [secondary.x, secondary.y]

    const coord = {
      x: this._getMinMax(primaryCoordinate, secondaryCoordinate, 0),
      y: this._getMinMax(primaryCoordinate, secondaryCoordinate, 1),
    }

    if (coord.y.max < coord.x.max) {
      coord.y.min = primary.y - (coord.x.max - coord.x.min) / 2
      coord.y.max = primary.y + (coord.x.max - coord.x.min) / 2
    } else {
      coord.x.min = primary.x - (coord.y.max - coord.y.min) / 2
      coord.x.max = primary.x + (coord.y.max - coord.y.min) / 2
    }

    coord.x.min -= padding
    coord.x.max += padding

    coord.y.min -= padding
    coord.y.max += padding

    coord.x.min = this._fixFloatingPoint(coord.x.min)
    coord.x.max = this._fixFloatingPoint(coord.x.max)
    coord.y.min = this._fixFloatingPoint(coord.y.min)
    coord.y.max = this._fixFloatingPoint(coord.y.max)

    // The mid points are just the halfway points between two coordinates
    coord.x.midPoint = (coord.x.max + coord.x.min) / 2
    coord.y.midPoint = (coord.y.max + coord.y.min) / 2

    coord.x.midPoint = this._fixFloatingPoint(coord.x.midPoint)
    coord.y.midPoint = this._fixFloatingPoint(coord.y.midPoint)

    return coord
  }

  _getPointFormatter() {
    const point = {
      x: this.point.x.toFixed(2) + '%',
      y: this.point.y.toFixed(2) + '%',
    }

    return [
      '<div class="u-paddingTop2gu u-paddingRight2gu u-paddingBottom2gu u-paddingLeft2gu">',
      '<h4 class="u-paddingBottom2gu u-paddingLeft4gu">' +
        this.series.name +
        '</h4>',
      '<table class="Table" style="min-width: 200px;">',
      '<tr class="Table-row" style="background-color: transparent;">',
      '<td class="Table-cell u-paddingBottom0 u-paddingTop2gu u-paddingLeft4gu u-textTight">',
      'Return',
      '</td>',
      '<td class="Table-cell u-paddingBottom0 u-paddingTop2gu u-paddingLeft4gu u-textRight u-textTight">',
      point.y,
      '</td>',
      '</tr>',
      '<tr class="Table-row" style="background-color: transparent;">',
      '<td class="Table-cell u-paddingBottom0 u-paddingTop2gu u-paddingLeft4gu u-textTight">',
      'Standard Deviation',
      '</td>',
      '<td class="Table-cell u-paddingBottom0 u-paddingTop2gu u-paddingLeft4gu u-textRight u-textTight">',
      point.x,
      '</td>',
      '</tr>',
      '</table>',
      '<h4>&nbsp;</h4>',
      '</div>',
    ].join('')
  }

  _renderData(chartId, seriesData, coord) {
    const chartData = {
      chart: {
        height: '100%',
        plotBackgroundColor: 'rgba(35, 31, 32, 0.05)',
        plotBorderColor: 'rgba(35, 31, 32, 0.20)',
        plotBorderWidth: 1,
        style: {
          fontFamily: 'Franklin Gothic Condensed',
          fontSize: '16px',
        },
        spacingLeft: 0,
        spacingRight: 62,
        type: 'scatter',
        // width: null
      },
      credits: false,
      legend: {
        align: 'left',
        itemMarginBottom: 5,
        itemStyle: {
          color: 'rgb(35, 31, 32)',
          fontSize: '16px',
          textOverflow: null,
        },
        padding: 0,
        margin: 10,
        symbolPadding: 4,
        x: 45,
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            states: {
              hover: {
                enabled: false,
                lineColor: 'rgb(100,100,100)',
              },
            },
          },
        },
        series: {
          dataLabels: {
            allowOverlap: true,
            enabled: false,
            format: '{series.name}',
          },
        },
      },
      series: seriesData,
      title: {
        text: '',
      },
      tooltip: {
        backgroundColor: 'rgba(255,255,255, 0.95)',
        borderColor: 'rgba(35, 31, 32, 0.3)',
        borderRadius: 5,
        padding: 0,
        formatter: this._getPointFormatter,
        shadow: false,
        shared: true,
        useHTML: true,
      },
      xAxis: {
        endOnTick: false,
        gridLineWidth: 0,
        labels: {
          format: '{value}%',
          style: {
            color: 'rgba(35, 31, 32, 0.80)',
            fontSize: '14px',
          },
          x: 3,
        },
        lineWidth: 0,
        max: coord.x.max,
        min: coord.x.min,
        minorGridLineWidth: 0,
        plotLines: [
          {
            color: 'rgba(35, 31, 32, 0.25)',
            dashStyle: 'dot',
            value: coord.x.midPoint,
            width: 2,
          },
        ],
        // showLastLabel: true,
        startOnTick: false,
        tickWidth: 0,
        title: {
          align: 'middle',
          text: 'Average Annual Standard Deviation (3-year)',
          style: {
            color: 'rgba(35, 31, 32, 0.40)',
            fontSize: '14px',
          },
        },
      },
      yAxis: {
        endOnTick: false,
        gridLineWidth: 0,
        labels: {
          format: '{value}%',
          style: {
            color: 'rgba(35, 31, 32, 0.80)',
            fontSize: '14px',
          },
        },
        lineWidth: 0,
        max: coord.y.max,
        min: coord.y.min,
        minorGridLineWidth: 0,
        plotLines: [
          {
            color: 'rgba(35, 31, 32, 0.25)',
            dashStyle: 'dot',
            value: coord.y.midPoint,
            width: 2,
          },
        ],
        startOnTick: false,
        tickWidth: 0,
        title: {
          align: 'middle',
          text: 'Average Annual Return (3-year)',
          style: {
            color: 'rgba(35, 31, 32, 0.40)',
            fontSize: '14px',
          },
        },
      },
    }

    Highcharts.chart(chartId, chartData)
  }

  /*
   * Scatter Plot Chart Requirements
   * This chart measures Rate of Return (y-axis) in relation to the 3-year avg annual standard deviation (x-axis).
   * It contains two points:
   * 1. Index: its values will be centered on the grid (the lighter color.)
   * 2. Fund: its values will be offset compared to the center (the darker color.)
   * It needs to scale according to its min/max sizes and retain its ratio such that both points are visible.
   */
  renderScatterPlotChart(id) {
    const {
      indexStdDev,
      indexValue,
      fundStdDev,
      netAssetValue,
      name,
      indexName,
    } = this.data
    const chartId = id || 'scatter-plot-chart' // add suffix to differentiate if necessary.

    const primary = {
      x: parseFloat(indexStdDev), // 4
      y: parseFloat(indexValue), // 8
    }

    const secondary = {
      x: parseFloat(fundStdDev), // 2
      y: parseFloat(netAssetValue), // 1
    }

    const coord = this._getCoordinates(primary, secondary, 10)

    const seriesData = [
      {
        color: '#163153',
        data: [[secondary.x, secondary.y]],
        height: 12,
        name: name + ' NAV',
        width: 12,
      },
      {
        color: '#4698cb',
        data: [[primary.x, primary.y]],
        name: indexName,
      },
      {
        data: [
          {
            dataLabels: {
              enabled: true,
              defer: false,
              format: '{point.name}',
              style: {
                color: 'rgb(35, 31, 32, 0.6)',
                fontSize: '14px',
                fontWeight: 'normal',
                textOutline: 'none',
              },
              x: 50,
              y: 2,
            },
            name: 'Higher Returns',
            x: coord.x.midPoint,
            y: coord.y.max,
          },
          {
            dataLabels: {
              enabled: true,
              defer: false,
              format: '{point.name}',
              style: {
                color: 'rgb(35, 31, 32, 0.6)',
                fontSize: '14px',
                fontWeight: 'normal',
                textOutline: 'none',
              },
              x: -10,
              y: 27,
            },
            name: 'Less Volatile',
            x: coord.x.min,
            y: coord.y.midPoint,
          },
          {
            dataLabels: {
              enabled: true,
              defer: false,
              format: '{point.name}',
              style: {
                color: 'rgb(35, 31, 32, 0.6)',
                fontSize: '14px',
                fontWeight: 'normal',
                textOutline: 'none',
              },
              x: 10,
              y: -5,
            },
            name: 'More Volatile',
            x: coord.x.max,
            y: coord.y.midPoint,
          },
          {
            dataLabels: {
              enabled: true,
              defer: false,
              format: '{point.name}',
              style: {
                color: 'rgb(35, 31, 32, 0.6)',
                fontSize: '14px',
                fontWeight: 'normal',
                textOutline: 'none',
              },
              x: -48,
              y: -3,
            },
            name: 'Lower Returns',
            x: coord.x.midPoint,
            y: coord.y.min,
          },
        ],
        enableMouseTracking: false,
        keys: ['x', 'y', 'name'],
        linkedTo: 0,
        marker: {
          enabled: false,
        },
      },
    ]

    this._renderData(chartId, seriesData, coord)
  }
}

export default RiskReturnChart
